import React, { useState, useEffect } from 'react';
import { Key, Globe, Check, X, AlertCircle, Loader, Edit2, Trash2 } from 'lucide-react';
import apiService from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import { GoDaddyCredentialsCreate, GoDaddyCredentialsStatus, GoDaddyTestConnectionRequest } from '../../types';
import Tooltip from '../common/Tooltip';
import ConfirmationDialog from '../common/ConfirmationDialog';

interface GoDaddyCredentialsProps {
  projectId: number;
}

const GoDaddyCredentials: React.FC<GoDaddyCredentialsProps> = ({ projectId }) => {
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [environment, setEnvironment] = useState<'production' | 'ote'>('production');
  const [status, setStatus] = useState<GoDaddyCredentialsStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Track whether the environment selector has been initialized from saved
  // credentials yet, so we only sync once per project load (not on every
  // user-driven environment change).
  const [environmentInitialized, setEnvironmentInitialized] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load credential status on mount and when environment or projectId changes
  useEffect(() => {
    loadCredentialStatus();
  }, [projectId, environment]);

  // Reset initialization when switching projects
  useEffect(() => {
    setEnvironmentInitialized(false);
  }, [projectId]);

  const loadCredentialStatus = async () => {
    try {
      setIsLoading(true);
      const credStatus = await apiService.getGoDaddyCredentialsStatus(projectId);

      console.log('[GoDaddyCredentials] Loaded status:', credStatus);

      // ALWAYS show credential status, even if environment doesn't match
      // This provides visibility into what credentials exist across all environments
      setStatus(credStatus);

      // On first load for this project, sync the environment selector to
      // match the saved credentials so the user sees the "Configured" view
      // instead of the empty form on a mismatched environment.
      if (!environmentInitialized && credStatus.configured && credStatus.environment) {
        const savedEnv = credStatus.environment as 'production' | 'ote';
        if (savedEnv === 'production' || savedEnv === 'ote') {
          setEnvironment(savedEnv);
        }
        setEnvironmentInitialized(true);
      } else if (!environmentInitialized) {
        setEnvironmentInitialized(true);
      }
    } catch (err: any) {
      console.error('[GoDaddyCredentials] Failed to load status:', err);
      // On error, assume not configured
      setStatus({
        configured: false,
        environment: environment,
        encrypted: false,
        last_updated: undefined,
        api_key_preview: undefined
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!apiKey.trim() || !apiSecret.trim()) {
      setError('API Key and API Secret are required');
      return;
    }

    try {
      setIsLoading(true);
      const credentials: GoDaddyCredentialsCreate = {
        api_key: apiKey,
        api_secret: apiSecret,
        environment,
      };

      const result = await apiService.storeGoDaddyCredentials(projectId, credentials);
      setSuccessMessage(result.message || 'Credentials saved successfully');

      console.log('[GoDaddyCredentials] Save successful, exiting edit mode and updating status');

      // Exit edit mode FIRST - this is critical for triggering the configured view
      setIsEditMode(false);

      // Clear form inputs (but don't clear environment)
      setApiKey('');
      setApiSecret('');

      // Immediately set status to configured to trigger the view change
      // This forces the component to re-render with the configured view
      setStatus({
        configured: true,
        environment: environment,
        encrypted: true,
        last_updated: new Date().toISOString(),
        api_key_preview: undefined // Will be loaded from API
      });

      console.log('[GoDaddyCredentials] Status set to configured, view should update');

      // Then reload from API to get the api_key_preview and actual data
      await loadCredentialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDeleteCredentials = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);

      const result = await apiService.deleteGoDaddyCredentials(projectId);
      setSuccessMessage(result.message || 'Credentials deleted successfully');

      // Clear status and reset to initial state
      setStatus(null);
      setIsEditMode(false);
      setApiKey('');
      setApiSecret('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete credentials');
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setError(null);
    setSuccessMessage(null);

    // If credentials are configured, always use saved credentials
    const useSavedCredentials = status?.configured || false;

    // Validate that we have credentials to test
    if (!useSavedCredentials && (!apiKey.trim() || !apiSecret.trim())) {
      setError('Please enter credentials or save them first before testing.');
      setIsTesting(false);
      return;
    }

    try {
      let result;

      if (useSavedCredentials) {
        // Test using saved credentials from database
        result = await apiService.testGoDaddyConnection(
          projectId,
          { environment } as GoDaddyTestConnectionRequest,
          true
        );
      } else {
        // Test using form credentials (not saved yet)
        const testRequest: GoDaddyTestConnectionRequest = {
          api_key: apiKey,
          api_secret: apiSecret,
          environment,
        };
        result = await apiService.testGoDaddyConnection(projectId, testRequest);
      }

      if (result.success) {
        const credentialsSource = useSavedCredentials ? ' (using saved credentials)' : ' (using form credentials)';
        setSuccessMessage(result.message + credentialsSource);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      // Handle different error types
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Invalid GoDaddy API credentials. Please check your API key and secret.');
      } else if (err.response?.status === 404 && err.response?.data?.detail?.includes('No GoDaddy credentials')) {
        setError('No saved credentials found. Please save credentials first.');
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Connection timeout. Please check your network connection and try again.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Connection test failed');
      }
    } finally {
      setIsTesting(false);
    }
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
    setError(null);
    setSuccessMessage(null);
    // Don't pre-fill credentials for security - user must re-enter
    setApiKey('');
    setApiSecret('');
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setApiKey('');
    setApiSecret('');
    setError(null);
    setSuccessMessage(null);
  };

  // Determine if we should show the form or the configured view
  const showConfiguredView = status?.configured && !isEditMode;
  const showForm = !status?.configured || isEditMode;

  // Debug logging for view state
  console.log('[GoDaddyCredentials] View state:', {
    status,
    isEditMode,
    showConfiguredView,
    showForm
  });

  // Detect environment mismatch between saved credentials and current selector
  const hasEnvironmentMismatch = status?.configured && status.environment !== environment;

  return (
    <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6">
      <div className="flex items-center mb-4">
        <Globe className="w-5 h-5 text-primary-600 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">GoDaddy API Configuration</h2>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Configure your GoDaddy API credentials to enable domain management tools for your agents.
      </p>

      {/* Environment Selector - Always visible */}
      <div className="mb-6">
        <label htmlFor="godaddy-environment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Environment
        </label>
        <CustomSelect
          value={environment}
          onChange={(value) => setEnvironment(value as 'production' | 'ote')}
          options={[
            { value: 'production', label: 'Production' },
            { value: 'ote', label: 'OTE (Sandbox)' },
          ]}
          disabled={isLoading || isTesting}
          aria-label="Environment"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Use OTE for testing without affecting real domains
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start space-x-2">
          <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-800 dark:text-green-300">{successMessage}</p>
        </div>
      )}

      {/* Environment Mismatch Warning */}
      {hasEnvironmentMismatch && (
        <div className="mb-4 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-primary-500 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-2">
                Environment Mismatch
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">
                These credentials are configured for <strong>{status.environment === 'ote' ? 'OTE (Sandbox)' : 'Production'}</strong> environment,
                but you're currently viewing the <strong>{environment === 'ote' ? 'OTE (Sandbox)' : 'Production'}</strong> selector.
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                To test or edit these credentials, change the environment selector above to match the saved environment.
              </p>
              <button
                onClick={() => setEnvironment(status.environment as 'production' | 'ote')}
                className="text-sm text-primary-600 hover:text-primary-800 underline font-medium"
              >
                Switch to {status.environment === 'ote' ? 'OTE (Sandbox)' : 'Production'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configured Status View */}
      {showConfiguredView && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-2 flex-1">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  {status.environment === 'production' ? 'Production' : 'OTE (Sandbox)'} Credentials Configured
                </p>
                {status.api_key_preview && (
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    API Key: <span className="font-mono">{status.api_key_preview}</span>
                  </p>
                )}
                {status.last_updated && (
                  <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                    Last updated: {new Date(status.last_updated).toLocaleString()}
                  </p>
                )}
                {status.encrypted && (
                  <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                    Credentials are encrypted and stored securely
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons for Configured State */}
          <div className="flex items-center space-x-3 mt-4">
            <div className="relative group">
              <Tooltip content={hasEnvironmentMismatch ? 'Switch environment selector to match saved credentials' : ''}>
              <button
                onClick={handleTestConnection}
                disabled={isTesting || isLoading || hasEnvironmentMismatch}
                className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/30 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isTesting && <Loader className="w-4 h-4 animate-spin" />}
                <span>{isTesting ? 'Testing...' : 'Test Connection'}</span>
              </button>
              </Tooltip>
              {hasEnvironmentMismatch && (
                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                  Switch environment selector to match saved credentials
                </div>
              )}
            </div>

            <div className="relative group">
              <Tooltip content={hasEnvironmentMismatch ? 'Switch environment selector to match saved credentials' : ''}>
              <button
                onClick={handleEnterEditMode}
                disabled={isLoading || isTesting || hasEnvironmentMismatch}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit Credentials</span>
              </button>
              </Tooltip>
              {hasEnvironmentMismatch && (
                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                  Switch environment selector to match saved credentials
                </div>
              )}
            </div>

            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading || isTesting}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* Credential Form - Show when not configured or in edit mode */}
      {showForm && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isLoading && !isTesting && apiKey.trim() && apiSecret.trim()) {
              handleSaveCredentials();
            }
          }}
        >
          {isEditMode && (
            <div className="mb-4 p-3 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Enter your new credentials below. For security reasons, existing credentials are not displayed.
              </p>
            </div>
          )}

          {/* API Key */}
          <div>
            <label htmlFor="godaddy-api-key" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              API Key
            </label>
            <div className="relative">
              <input
                id="godaddy-api-key"
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your GoDaddy API Key"
                className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={isLoading}
              />
              <Key className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            </div>
          </div>

          {/* API Secret */}
          <div>
            <label htmlFor="godaddy-api-secret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              API Secret
            </label>
            <div className="relative">
              <input
                id="godaddy-api-secret"
                type="password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="Enter your GoDaddy API Secret"
                className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={isLoading}
              />
              <Key className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Your API secret is encrypted before storage and never displayed
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-2">
            <button
              type="submit"
              disabled={isLoading || isTesting || !apiKey.trim() || !apiSecret.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading && <Loader className="w-4 h-4 animate-spin" />}
              <span>{isEditMode ? 'Save Changes' : 'Save Credentials'}</span>
            </button>

            {isEditMode && (
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            )}

            {/* Test Connection - Show if form has values */}
            {!isEditMode && (apiKey.trim() && apiSecret.trim()) && (
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={isTesting || isLoading}
                className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/30 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isTesting && <Loader className="w-4 h-4 animate-spin" />}
                <span>{isTesting ? 'Testing...' : 'Test Connection'}</span>
              </button>
            )}
          </div>
        </form>
      )}

      {/* Help Text */}
      <div className="mt-6 p-4 bg-primary-50 dark:bg-gray-800 border border-primary-200 dark:border-gray-700 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-2">
          How to get your GoDaddy API credentials:
        </p>
        <ol className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
          <li>Visit the GoDaddy Developer Portal</li>
          <li>Sign in with your GoDaddy account</li>
          <li>Create a new API key</li>
          <li>Copy your API Key and API Secret</li>
          <li>Paste them above and save</li>
        </ol>
      </div>

      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete GoDaddy credentials?"
          message="Are you sure you want to delete your GoDaddy credentials? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          isLoading={isLoading}
          onConfirm={confirmDeleteCredentials}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default GoDaddyCredentials;
